import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { Link, useLocation } from 'react-router-dom';

const TEXT_JA = {
  breadcrumb: { home: 'ホーム', filament: 'フィラメントデータ', title: '曲げ強度試験方法' },
  title: '曲げ強度試験方法',
  overview: {
    title: '概要',
    content: '3Dプリント材料の曲げ強度を測定するための標準的な試験方法について説明します。この試験により、材料の機械的特性を定量的に評価することができます。'
  },
  machine: {
    title: '自作3点曲げ試験機',
    p1: '本ページで使用している3点曲げ試験機は、市販の万能試験機ではなく、管理人が自作した簡易的な装置です。絶対値としての精度は高くありませんが、「同じ条件で造形した試験片同士の比較」を行う目的であれば、十分な再現性が得られることを想定しています。',
    p2: '荷重を加える高さ方向の制御には、ステッピングモーターとリードシャフトを使用しています。モーターの回転角度からリードシャフトの送り量を計算し、おおよその変位量を制御する方式です。',
    p3: '荷重は、定格100 kgのロードセルで測定しています。ロードセルからの信号を読み取り、試験中の荷重変化を記録できるようにしています。',
    p4: '治具本体（ベースや保持部など）の多くは PLA-CF で3Dプリントしており、試験片と接する部分（支点の接触面および中央の押し子）には、直径φ10 mmのSUS製ダボピンを取り付けています。プラスチック部分が摩耗・変形しないようにしつつ、試験片にはできるだけシンプルな接触条件となるよう配慮しています。',
    p5: '以上のように、あくまで「自作の簡易試験機」であるため、ここで得られる数値は厳密な試験規格に完全に準拠するものではありません。本サイトでは、',
    p5Strong: '同じ治具・同じ条件のもとで造形条件や材料を変えたときの相対比較',
    p5End: 'を主な用途としています。'
  },
  specimen: {
    title: '試験片（形状・印刷条件）',
    intro: '試験片はISO 178の曲げ試験を参考にしています。寸法や印刷条件は、以降のデータ比較で同じ前提になります。',
    imgAlt: '曲げ試験用の試験片。写真内にパターン表記があり、黒い線で印刷方向が示されている',
    imgCaption: '試験片の外観（写真内の表記がパターン1〜3に対応）。黒い線は印刷方向を示しています。',
    shapeTitle: '共通（形状）',
    length: '長さ',
    width: '幅',
    thickness: '厚さ',
    printTitle: '共通（印刷条件）',
    layerHeight: '積層ピッチ（レイヤー高さ）：',
    nozzle: 'ノズル径：',
    speed: '造形速度：',
    walls: '底面層・天面層・外周壁：',
    wallsValue: 'いずれも設定しない',
    wallsNote: '（外周シェルを設けず、断面全体をインフィルのみで構成）',
    infill: 'インフィル：',
    infillValue: '100%（ハッチパターン）',
    postProcess: '後処理：',
    postProcessValue: '特に実施せず',
    postProcessNote: '（造形後の追加研磨や表面処理は行っていない）',
    patternTitle: 'パターン別',
    patternIntro: '積層方向による違いを比較するために、同じ形状の試験片を次の3パターンで造形しています。',
    pattern1: 'パターン1：',
    pattern1Title: '長手方向とレイヤー方向が同一直線',
    pattern1Desc: '試験片の長手方向をベッド面に対して水平に配置し、造形時のスジ（レイヤーライン）が長手方向と同じ向き（平行）になるように印刷します。',
    pattern2: 'パターン2：',
    pattern2Title: '長手方向に対してレイヤー方向を斜めに配置',
    pattern2Desc: '試験片の長手方向をベッド面に対して水平に配置しつつ、レイヤーラインが長手方向に対して斜めになるように印刷します（例：45°前後の角度で積層方向を傾けるイメージ）。',
    pattern3: 'パターン3：',
    pattern3Title: '長手方向を垂直にして印刷',
    pattern3Desc: '試験片の長手方向がベッド面に対して垂直になるように、立てた状態で印刷します。曲げ荷重に対して、層間方向がもっとも厳しくなる向きです。',
    patternNote: '※写真中の黒い線は、印刷方向（造形時の向き）を示しています。パターンの違いは、この向きとレイヤー方向の関係として整理しています。'
  },
  method: {
    title: '測定方法',
    p1: '本ページの曲げ強度試験は、あくまで「同じ条件で造形した試験片同士の比較」を目的とした、簡易的な評価です。専用の試験室や厳密な試験規格に完全に従った測定ではなく、自宅環境で可能な範囲の実験として位置づけています。',
    p2: '試験片の外形寸法（厚みや幅など）は、モデリング時の設定値どおりとみなし、',
    p2Strong: '試験前に個々の試験片の寸法測定は行っていません',
    p2End: '。そのため、ここで示す値は「設計寸法ベースで計算したときの曲げ強度の比較」として扱ってください。',
    p3: '試験環境については、',
    p3Strong: '温度や湿度を専用の装置で管理できる環境ではありません',
    p3End: '。自宅の作業部屋で実施しているため、室温・湿度はその時々の環境に依存します。空調や除湿器のオン／オフによる極端な変化は避けていますが、厳密な環境試験ではない点にご注意ください。',
    p4: '測定は基本的に、',
    p4Strong: '同じ条件で造形した試験片3本',
    p4End: 'を用いて行っています。それぞれの最大荷重を記録し、傾向を確認するための参考値として扱っています。',
    p5: 'なお、本サイトでは、',
    p5Strong: '標準偏差や統計的なバラツキ評価は行っていません',
    p5End: '。また、破断面の詳細な観察や破壊様式の分類も実施しておらず、ここでは「どの条件／材料が相対的に強そうか」をざっくり比較するためのデータとして公開しています。',
    formulaTitle: '計算式',
    formula: '曲げ強度 σ = 3FL / (2bh²)',
    f: 'F：最大荷重 (N)',
    l: 'L：支点間距離 (mm)',
    b: 'b：試験片幅 (mm)',
    h: 'h：試験片厚さ (mm)'
  },
  caution: {
    title: '注意点',
    important: '重要：',
    p1: '本サイトの測定結果は、学術的な研究や製品設計の根拠として使用することは想定していません。あくまで個人的な興味・検証の範囲での参考データとしてご覧ください。',
    p2: '厳密な材料試験が必要な場合は、JIS規格やISO規格に準拠した試験機関での測定を推奨します。'
  },
  diagram: {
    title: '試験概要図',
    alt: '3点曲げ試験の概要図',
    caption: '3点曲げ試験の基本構成：試験片を2点で支持し、中央から荷重を加える'
  },
  standards: {
    title: '参考規格',
    iso: 'ISO 178：プラスチック - 曲げ特性の求め方',
    astm: 'ASTM D790：プラスチックの曲げ特性試験方法',
    jis: 'JIS K 7171：プラスチック - 曲げ特性の試験方法'
  },
  dataUsage: {
    title: 'データ利用について',
    subtitle: '個人利用は自由ですが、公開する場合は必ずお問い合わせください',
    content: '本サイトで公開している試験データ（曲げ強度、応力-ひずみ曲線、CSV等）は、個人的な材料選定や学習目的での利用は自由です。ただし、',
    contentStrong: '動画・記事・SNS等での公開、データの転載・再配布を行う場合は必ず事前にお問い合わせください。',
    detail: '詳細は',
    linkText: 'データ利用規約',
    detailEnd: 'をご確認ください。'
  }
};

const TEXT_EN = {
  breadcrumb: { home: 'Home', filament: 'Filament Data', title: 'Bending Strength Test Method' },
  title: 'Bending Strength Test Method',
  overview: {
    title: 'Overview',
    content: 'This page describes the standard test method for measuring the flexural strength of 3D printing materials. This test allows quantitative evaluation of the mechanical properties of materials.'
  },
  machine: {
    title: 'DIY Three-Point Bending Test Machine',
    p1: 'The three-point bending test machine used on this page is not a commercial universal testing machine, but a simple device built by the site administrator. While the absolute accuracy is not high, it is expected to provide sufficient reproducibility for the purpose of "comparing test specimens fabricated under the same conditions."',
    p2: 'The vertical control for applying load uses a stepping motor and lead shaft. The approximate displacement is controlled by calculating the lead shaft feed from the motor rotation angle.',
    p3: 'The load is measured with a load cell rated at 100 kg. The signal from the load cell is read to record load changes during testing.',
    p4: 'Most of the fixture body (base and holding parts) is 3D printed with PLA-CF, and the parts in contact with the test specimen (support point contact surfaces and center pusher) are fitted with SUS dowel pins with a diameter of φ10 mm. This prevents wear and deformation of plastic parts while providing the simplest possible contact conditions for the test specimen.',
    p5: 'As described above, since this is a "DIY simple testing machine," the values obtained here do not fully comply with strict test standards. On this site, ',
    p5Strong: 'relative comparison when changing fabrication conditions or materials under the same fixture and conditions',
    p5End: ' is the main purpose.'
  },
  specimen: {
    title: 'Test Specimen (Shape and Print Conditions)',
    intro: 'The test specimens are based on ISO 178 bending test. Dimensions and print conditions are the same premise for subsequent data comparisons.',
    imgAlt: 'Test specimen for bending test. Pattern notation is shown in the photo, and black lines indicate print direction',
    imgCaption: 'Appearance of test specimen (notation in photo corresponds to patterns 1-3). Black lines indicate print direction.',
    shapeTitle: 'Common (Shape)',
    length: 'Length',
    width: 'Width',
    thickness: 'Thickness',
    printTitle: 'Common (Print Conditions)',
    layerHeight: 'Layer height:',
    nozzle: 'Nozzle diameter:',
    speed: 'Print speed:',
    walls: 'Bottom layers, top layers, and perimeter walls:',
    wallsValue: 'None set',
    wallsNote: '(No perimeter shells, entire cross-section composed of infill only)',
    infill: 'Infill:',
    infillValue: '100% (hatch pattern)',
    postProcess: 'Post-processing:',
    postProcessValue: 'None performed',
    postProcessNote: '(No additional sanding or surface treatment after fabrication)',
    patternTitle: 'By Pattern',
    patternIntro: 'To compare differences due to layer direction, test specimens of the same shape are fabricated in the following three patterns.',
    pattern1: 'Pattern 1:',
    pattern1Title: 'Longitudinal direction and layer direction are collinear',
    pattern1Desc: 'The longitudinal direction of the test specimen is placed horizontal to the bed surface, and printed so that the fabrication lines (layer lines) are in the same direction (parallel) as the longitudinal direction.',
    pattern2: 'Pattern 2:',
    pattern2Title: 'Layer direction placed diagonally to longitudinal direction',
    pattern2Desc: 'The longitudinal direction of the test specimen is placed horizontal to the bed surface, while the layer lines are printed diagonally to the longitudinal direction (e.g., tilting the layer direction at an angle around 45°).',
    pattern3: 'Pattern 3:',
    pattern3Title: 'Print with longitudinal direction vertical',
    pattern3Desc: 'The test specimen is printed in an upright position with the longitudinal direction perpendicular to the bed surface. This is the most severe orientation for interlayer direction against bending load.',
    patternNote: '* Black lines in the photo indicate print direction (orientation during fabrication). Pattern differences are organized as the relationship between this orientation and layer direction.'
  },
  method: {
    title: 'Measurement Method',
    p1: 'The bending strength test on this page is a simple evaluation for the purpose of "comparing test specimens fabricated under the same conditions." It is not a measurement that fully follows strict test standards in a dedicated test room, but is positioned as an experiment within the range possible in a home environment.',
    p2: 'The external dimensions of the test specimen (thickness, width, etc.) are assumed to be as set during modeling, and ',
    p2Strong: 'dimensional measurements of individual test specimens are not performed before testing',
    p2End: '. Therefore, the values shown here should be treated as "comparison of bending strength when calculated based on design dimensions."',
    p3: 'Regarding the test environment, ',
    p3Strong: 'it is not an environment where temperature and humidity can be controlled with dedicated equipment',
    p3End: '. Since testing is conducted in a home workspace, room temperature and humidity depend on the environment at the time. While extreme changes due to air conditioning or dehumidifier on/off are avoided, please note that this is not a strict environmental test.',
    p4: 'Measurements are basically performed using ',
    p4Strong: 'three test specimens fabricated under the same conditions',
    p4End: '. The maximum load of each is recorded and treated as reference values for confirming trends.',
    p5: 'Note that on this site, ',
    p5Strong: 'standard deviation and statistical variation evaluation are not performed',
    p5End: '. Also, detailed observation of fracture surfaces and classification of failure modes are not conducted, and data is published here for roughly comparing "which conditions/materials seem relatively stronger."',
    formulaTitle: 'Formula',
    formula: 'Flexural strength σ = 3FL / (2bh²)',
    f: 'F: Maximum load (N)',
    l: 'L: Support span (mm)',
    b: 'b: Specimen width (mm)',
    h: 'h: Specimen thickness (mm)'
  },
  caution: {
    title: 'Cautions',
    important: 'Important:',
    p1: 'The measurement results on this site are not intended for use as a basis for academic research or product design. Please view them as reference data within the scope of personal interest and verification.',
    p2: 'For strict material testing, measurement at a testing institution compliant with JIS or ISO standards is recommended.'
  },
  diagram: {
    title: 'Test Overview Diagram',
    alt: 'Overview diagram of three-point bending test',
    caption: 'Basic configuration of three-point bending test: Support specimen at two points and apply load from center'
  },
  standards: {
    title: 'Reference Standards',
    iso: 'ISO 178: Plastics - Determination of flexural properties',
    astm: 'ASTM D790: Standard Test Methods for Flexural Properties of Plastics',
    jis: 'JIS K 7171: Plastics - Determination of flexural properties'
  },
  dataUsage: {
    title: 'About Data Usage',
    subtitle: 'Personal use is free, but please contact us before public disclosure',
    content: 'Test data published on this site (flexural strength, stress-strain curves, CSV, etc.) is free for personal material selection and learning purposes. However, ',
    contentStrong: 'if you publish on videos, articles, SNS, etc., or redistribute data, please contact us in advance.',
    detail: 'For details, please see ',
    linkText: 'Data Usage Policy',
    detailEnd: '.'
  }
};

const BendStrengthTestPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en/');
  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;
  const prefix = isEnglish ? '/en' : '';

  const breadcrumbItems = [
    { label: TEXT.breadcrumb.home, path: `${prefix}/` },
    { label: TEXT.breadcrumb.filament, path: `${prefix}/filament` },
    { label: TEXT.breadcrumb.title, path: `${prefix}/filament/bend-strength-test` }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* タイトル */}
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                {TEXT.title}
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 概要 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.overview.title}
              </h2>
              <p className="text-metal-light leading-relaxed">
                {TEXT.overview.content}
              </p>
            </div>

            {/* 自作3点曲げ試験機 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.machine.title}
              </h2>
              <div className="text-metal-light space-y-4 leading-relaxed">
                <p>{TEXT.machine.p1}</p>
                <p>{TEXT.machine.p2}</p>
                <p>{TEXT.machine.p3}</p>
                <p>{TEXT.machine.p4}</p>
                <p>
                  {TEXT.machine.p5}
                  <strong className="text-white">{TEXT.machine.p5Strong}</strong>
                  {TEXT.machine.p5End}
                </p>
              </div>
            </div>

            {/* 試験片（形状・印刷条件） */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.specimen.title}
              </h2>
              <div className="space-y-6">
                <p className="text-metal-light leading-relaxed">
                  {TEXT.specimen.intro}
                </p>

                {/* 試験片写真 */}
                <figure className="my-6">
                  <img
                    src={isEnglish ? "/assets/bending/specimen_en.jpg" : "/assets/bending/specimen.jpg"}
                    alt={TEXT.specimen.imgAlt}
                    className="w-full max-w-3xl mx-auto rounded-lg object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="mt-2 text-sm text-metal-light text-center">
                    {TEXT.specimen.imgCaption}
                  </figcaption>
                </figure>

                {/* 共通（形状） */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.specimen.shapeTitle}</h3>
                  <div className="bg-metal p-4 rounded max-w-md">
                    <table className="w-full text-sm">
                      <tbody className="text-metal-light">
                        <tr className="border-b border-gray-600">
                          <td className="py-2 text-white">{TEXT.specimen.length}</td>
                          <td className="py-2">80mm</td>
                        </tr>
                        <tr className="border-b border-gray-600">
                          <td className="py-2 text-white">{TEXT.specimen.width}</td>
                          <td className="py-2">10mm</td>
                        </tr>
                        <tr>
                          <td className="py-2 text-white">{TEXT.specimen.thickness}</td>
                          <td className="py-2">4mm</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 共通（印刷条件） */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.specimen.printTitle}</h3>
                  <ul className="space-y-1 text-sm text-metal-light">
                    <li>• {TEXT.specimen.layerHeight}<strong className="text-white">0.10 mm</strong></li>
                    <li>• {TEXT.specimen.nozzle}<strong className="text-white">0.40 mm</strong></li>
                    <li>• {TEXT.specimen.speed}<strong className="text-white">50 mm/s</strong></li>
                    <li>• {TEXT.specimen.walls}<strong className="text-white">{TEXT.specimen.wallsValue}</strong>{TEXT.specimen.wallsNote}</li>
                    <li>• {TEXT.specimen.infill}<strong className="text-white">{TEXT.specimen.infillValue}</strong></li>
                    <li>• {TEXT.specimen.postProcess}<strong className="text-white">{TEXT.specimen.postProcessValue}</strong>{TEXT.specimen.postProcessNote}</li>
                  </ul>
                </div>

                {/* パターン別 */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">{TEXT.specimen.patternTitle}</h3>
                  <p className="text-metal-light text-sm mb-3">
                    {TEXT.specimen.patternIntro}
                  </p>
                  <ul className="space-y-3 text-metal-light">
                    <li>
                      <strong className="text-white">{TEXT.specimen.pattern1}</strong>{TEXT.specimen.pattern1Title}
                      <p className="text-sm mt-1 ml-4">{TEXT.specimen.pattern1Desc}</p>
                    </li>
                    <li>
                      <strong className="text-white">{TEXT.specimen.pattern2}</strong>{TEXT.specimen.pattern2Title}
                      <p className="text-sm mt-1 ml-4">{TEXT.specimen.pattern2Desc}</p>
                    </li>
                    <li>
                      <strong className="text-white">{TEXT.specimen.pattern3}</strong>{TEXT.specimen.pattern3Title}
                      <p className="text-sm mt-1 ml-4">{TEXT.specimen.pattern3Desc}</p>
                    </li>
                  </ul>
                  <p className="text-sm text-metal-light mt-4">
                    {TEXT.specimen.patternNote}
                  </p>
                </div>
              </div>
            </div>

            {/* 測定方法 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.method.title}
              </h2>
              <div className="text-metal-light space-y-4 leading-relaxed">
                <p>{TEXT.method.p1}</p>
                <p>
                  {TEXT.method.p2}
                  <strong className="text-white">{TEXT.method.p2Strong}</strong>
                  {TEXT.method.p2End}
                </p>
                <p>
                  {TEXT.method.p3}
                  <strong className="text-white">{TEXT.method.p3Strong}</strong>
                  {TEXT.method.p3End}
                </p>
                <p>
                  {TEXT.method.p4}
                  <strong className="text-white">{TEXT.method.p4Strong}</strong>
                  {TEXT.method.p4End}
                </p>
                <p>
                  {TEXT.method.p5}
                  <strong className="text-white">{TEXT.method.p5Strong}</strong>
                  {TEXT.method.p5End}
                </p>
                
                <div className="bg-metal p-4 rounded mt-6">
                  <h3 className="text-white font-semibold mb-2">{TEXT.method.formulaTitle}</h3>
                  <div className="text-metal-light">
                    <p className="mb-2">{TEXT.method.formula}</p>
                    <ul className="text-sm space-y-1">
                      <li>{TEXT.method.f}</li>
                      <li>{TEXT.method.l}</li>
                      <li>{TEXT.method.b}</li>
                      <li>{TEXT.method.h}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 注意点 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.caution.title}
              </h2>
              <div className="text-metal-light space-y-4">
                <p>
                  <strong className="text-white">{TEXT.caution.important}</strong>
                  {TEXT.caution.p1}
                </p>
                <p>{TEXT.caution.p2}</p>
              </div>
            </div>

            {/* 模式図 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.diagram.title}
              </h2>
              <div className="bg-metal p-6 rounded text-center">
                <img 
                  src="/assets/bending/iso178_diagram.png"
                  alt={TEXT.diagram.alt}
                  className="w-full max-w-2xl mx-auto rounded object-cover"
                />
                <p className="text-metal-light text-sm mt-3">
                  {TEXT.diagram.caption}
                </p>
              </div>
            </div>

            {/* 参考規格 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.standards.title}
              </h2>
              <div className="text-metal-light space-y-2">
                <p>• {TEXT.standards.iso}</p>
                <p>• {TEXT.standards.astm}</p>
                <p>• {TEXT.standards.jis}</p>
              </div>
            </div>

            {/* データ利用に関する注意書き */}
            <div className="metal-panel p-6 border-2 border-accent/50">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2 flex items-center gap-2">
                <i className="ri-alert-line text-accent"></i>
                {TEXT.dataUsage.title}
              </h2>
              <div className="flex items-start space-x-3 bg-accent/10 border border-accent/30 p-4 rounded">
                <i className="ri-alert-line text-accent text-xl mt-0.5 flex-shrink-0"></i>
                <div className="text-sm">
                  <p className="text-white font-semibold mb-2">{TEXT.dataUsage.subtitle}</p>
                  <p className="text-metal-light leading-relaxed">
                    {TEXT.dataUsage.content}
                    <strong className="text-white">{TEXT.dataUsage.contentStrong}</strong>
                  </p>
                  <p className="text-metal-light mt-3">
                    {TEXT.dataUsage.detail}
                    <Link to={`${prefix}/data-policy`} className="text-accent hover:text-accent-orange underline transition-colors font-semibold">{TEXT.dataUsage.linkText}</Link>
                    {TEXT.dataUsage.detailEnd}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BendStrengthTestPage;
